/* eslint-disable max-len */
import React from "react";
import {Link} from "react-router-dom";
import {Box} from "@mui/material";
import {PoliciesStyledBox} from "lib/components";

export default function PrivacyPolicy() {
  return (
    <PoliciesStyledBox>
      <Box sx={{width: {xs: "100%", sm: "90%"}}}>
        <h3>Privacy Policy</h3>
        <p>
          <strong>Last updated: April 5, 2025</strong>
        </p>

        <p>
          This Privacy Policy outlines how <strong>Maritime IT Solutions LLC</strong> (&quot;the Company,&quot;
          &quot;We,&quot; &quot;Us,&quot; or &quot;Our&quot;) collects, uses, and discloses Your information when You
          use <strong>CrewBook</strong> (the &quot;Service&quot;). It also informs You of Your privacy rights and how
          the law protects You. By using the Service, You agree to the collection and use of Your information as
          described herein.
        </p>

        <p>
          We use Your Personal Data to provide and enhance the Service, which is designed to support seafarers and the
          maritime industry. Protecting Your data is a priority, and We are committed to safeguarding it in accordance
          with this policy.
        </p>

        <h4>Interpretation and Definitions</h4>
        <h5>Interpretation</h5>
        <p>
          Words with capitalized initial letters have specific meanings defined below. These definitions apply whether
          the terms appear in singular or plural form.
        </p>

        <h5>Definitions</h5>
        <dl>
          <dt>Account</dt>
          <dd>A unique profile created for You to access Our Service or its features.</dd>
          <dt>Company</dt>
          <dd>Maritime IT Solutions LLC, based in Delaware, USA.</dd>
          <dt>Cookies</dt>
          <dd>Small files placed on Your device by a website to store browsing details.</dd>
          <dt>Country</dt>
          <dd>United States of America.</dd>
          <dt>Device</dt>
          <dd>Any tool (e.g., computer, cellphone, or tablet) used to access the Service.</dd>
          <dt>Personal Data</dt>
          <dd>
            Information relating to an identifiable individual, including seafarer-specific details like professional
            certificates, identification numbers, and employment history.
          </dd>
          <dt>Service</dt>
          <dd>The CrewBook website.</dd>
          <dt>Service Provider</dt>
          <dd>Any person or entity processing data on Our behalf to support the Service.</dd>
          <dt>Third-party Social Media Service</dt>
          <dd>A platform allowing You to log in or create an Account for Our Service.</dd>
          <dt>Usage Data</dt>
          <dd>Automatically collected data from Your use of the Service (e.g., IP address, visit duration).</dd>
          <dt>Website</dt>
          <dd>
            CrewBook, accessible at <a href="https://www.crewbook.app">https://www.crewbook.app</a>.
          </dd>
          <dt>You</dt>
          <dd>The individual or entity using the Service.</dd>
        </dl>

        <h4>Collecting and Using Your Personal Data</h4>
        <h5>Types of Data Collected</h5>
        <h6>Personal Data</h6>
        <p>
          When You use Our Service, We may collect personally identifiable information to contact or identify You,
          including:
        </p>
        <ul>
          <li>Email address</li>
          <li>First and last name</li>
          <li>Phone number</li>
          <li>Address (State, Province, ZIP/Postal code, City)</li>
          <li>Seafarer-specific data (e.g., professional certificates, identification numbers, employment history)</li>
        </ul>

        <h6>Usage Data</h6>
        <p>We automatically collect Usage Data when You use the Service, such as:</p>
        <ul>
          <li>Your Device’s IP address</li>
          <li>Browser type and version</li>
          <li>Pages visited, visit time, and duration</li>
          <li>Unique device identifiers and diagnostic data</li>
        </ul>
        <p>
          For mobile access, We may also collect Your device type, mobile ID, operating system, and browser details.
        </p>

        <h5>Tracking Technologies and Cookies</h5>
        <p>
          We use Cookies and similar technologies (e.g., beacons, tags, scripts) to track activity and improve the
          Service. These include:
        </p>
        <ul>
          <li>
            <strong>Cookies</strong>: Small files on Your Device. You can refuse them via browser settings, though this
            may limit functionality.
          </li>
          <li>
            <strong>Flash Cookies</strong>: Store preferences locally; managed separately from browser settings (see{" "}
            <a href="https://www.adobe.com">Adobe’s guide</a>).
          </li>
          <li>
            <strong>Web Beacons</strong>: Small files in emails or pages to track visits and usage statistics.
          </li>
        </ul>
        <p>
          We use both <strong>Session Cookies</strong> (deleted when You close Your browser) and{" "}
          <strong>Persistent Cookies</strong> (retained offline) for:
        </p>
        <ul>
          <li>
            <strong>Necessary Cookies</strong>: Enable core features and authentication (Session, Administered by Us).
          </li>
          <li>
            <strong>Notice Acceptance Cookies</strong>: Track Your cookie consent (Persistent, Administered by Us).
          </li>
          <li>
            <strong>Functionality Cookies</strong>: Remember Your preferences (Persistent, Administered by Us).
          </li>
        </ul>
        <p>
          Learn more in Our Cookies Policy or visit <a href="https://www.allaboutcookies.org/">All About Cookies</a>.
        </p>

        <h5>Use of Your Personal Data</h5>
        <p>We may use Your Personal Data to:</p>
        <ul>
          <li>Provide and maintain the Service, including monitoring usage.</li>
          <li>Manage Your Account and registration.</li>
          <li>Fulfill contracts for products or services.</li>
          <li>Contact You via email, phone, SMS, or app notifications about updates or services.</li>
          <li>Send news or offers (unless You opt out).</li>
          <li>Handle Your requests.</li>
          <li>Facilitate business transfers (e.g., mergers).</li>
          <li>Analyze usage trends and improve the Service.</li>
        </ul>

        <h5>Sharing Your Personal Data</h5>
        <p>We may share Your information:</p>
        <ul>
          <li>
            With <strong>Service Providers</strong> for analysis or communication.
          </li>
          <li>
            During <strong>business transfers</strong> (e.g., mergers).
          </li>
          <li>
            With <strong>Affiliates</strong>, who must follow this policy.
          </li>
          <li>
            With <strong>business partners</strong> for offers.
          </li>
          <li>
            With <strong>other users</strong> in public areas or via Third-party Social Media Services.
          </li>
          <li>
            With Your <strong>consent</strong> for other purposes.
          </li>
        </ul>
        <p>
          For maritime users, We may share data (e.g., certificates) with employers or authorities for verification,
          always with Your consent or as legally required.
        </p>

        <h4>Retention of Your Personal Data</h4>
        <p>
          We retain Your Personal Data only as long as needed for the purposes outlined here, or to comply with legal
          obligations, resolve disputes, or enforce agreements. Usage Data is kept briefly for analysis, unless required
          longer for security or legal reasons.
        </p>

        <h4>Transfer of Your Personal Data</h4>
        <p>
          Your data may be processed in the USA or other locations where Our partners operate. This may involve
          transfers to jurisdictions with different data protection laws. By consenting to this policy, You agree to
          such transfers. We ensure secure handling and compliance with this policy wherever Your data goes.
        </p>

        <h4>Disclosure of Your Personal Data</h4>
        <ul>
          <li>
            <strong>Business Transactions</strong>: If We merge or sell assets, Your data may be transferred with
            notice.
          </li>
          <li>
            <strong>Law Enforcement</strong>: We may disclose data if legally required.
          </li>
          <li>
            <strong>Other Legal Needs</strong>: Disclosure may occur to comply with laws, protect rights, prevent
            wrongdoing, ensure safety, or limit liability.
          </li>
        </ul>

        <h4>Security of Your Personal Data</h4>
        <p>
          We use industry-standard encryption and access controls to protect Your data, especially sensitive seafarer
          information. However, no online method is 100% secure, and absolute security cannot be guaranteed.
        </p>

        <h4>Children&apos;s Privacy</h4>
        <p>
          Our Service is not for anyone under 13. We do not knowingly collect data from children under 13. If We
          discover such data without parental consent, We remove it. Parental consent may be required in some regions.
        </p>

        <h4>Links to Other Websites</h4>
        <p>
          Our Service may link to third-party sites We don’t control. Review their privacy policies, as We’re not
          responsible for their practices.
        </p>

        <h4>Changes to This Privacy Policy</h4>
        <p>
          We may update this policy, notifying You via email or a Service notice before changes take effect. The
          &quot;Last updated&quot; date will reflect revisions. Check back periodically.
        </p>

        <h4>Contact Us</h4>
        <p>Questions? Reach Us at:</p>
        <ul>
          <li>
            <strong>Email</strong>: <a href="mailto:watchkeeper@crewbook.app">watchkeeper@crewbook.app</a>
          </li>
          <li>
            <strong>Website</strong>: <Link to="/contact">https://www.crewbook.app/contact</Link>
          </li>
        </ul>
      </Box>
    </PoliciesStyledBox>
  );
}
